<template>
  <div class="h5-container">
    <nav class="navbar">
      <div class="brand">
        <span class="logo-text">洋么科技</span>
        <span class="badge">培养高级AI测开工程师</span>
      </div>
      <button class="consult-btn" @click="scrollToForm">点我试听AI测开</button>
    </nav>
    <header class="hero-section">
      <div class="hero-content">
        <h1 class="main-title">
          全栈AI测试开发<span class="title-split"> | </span><span class="sub-highlight">实战教程</span>
        </h1>
        <div class="feature-box">
          <div class="feature-item">
            <div class="feature-icon">
              <!-- 火箭图标，表示快速提升/技术进阶 -->
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M13 13l11-11L22 2l-11 11v4.5l4.5 4.5L22 13h-4.5L13 8.5V13z" fill="none" stroke="currentColor" stroke-width="2"/>
                <circle cx="7" cy="17" r="2" fill="currentColor"/>
                <path d="M10 20H4v-6" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <p style="text-align:left"><strong>训练垂直类测试模型</strong><br><span class="light-desc">非市场上堆提示词、调API的内容</span></p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <!-- 靶心图标，表示精准指导/一对一 -->
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
                <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" stroke-width="2"/>
                <circle cx="12" cy="12" r="2" fill="currentColor"/>
              </svg>
            </div>
            <p style="text-align:left"><strong>学习全程一对一推进</strong><br><span class="light-desc">学会为止，吃点好的 AI 测开技术</span></p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <!-- 盾牌图标，表示保障/无二次收费 -->
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M12 2L4 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-3z" fill="none" stroke="currentColor" stroke-width="2"/>
                <path d="M12 12l3-3" fill="none" stroke="currentColor" stroke-width="2"/>
                <path d="M12 12l-3-3" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <p style="text-align:left"><strong>无二次收费持续更新</strong><br><span class="light-desc">2023年开始研发，成熟落地经验</span></p>
          </div>
        </div>
        <div class="metrics-row">
          <div class="m-item">
            <span class="num">30<small>min</small></span>
            <span class="lbl">每天投入</span>
          </div>
          <div class="m-divider"></div>
          <div class="m-item">
            <span class="num">70<small>%</small></span>
            <span class="lbl">实战实操</span>
          </div>
          <div class="m-divider"></div>
          <div class="m-item">
            <span class="num">工作</span>
            <span class="lbl">学员内推</span>
          </div>
        </div>
        <div class="career-bar">
          <div class="career-label">学完就业方向，点击查看岗位提示</div>
          <transition name="tip-fade">
            <div class="career-tip" v-if="showTip">
              <div class="tip-content">
                <div class="tip-arrow" :style="{left: tipArrowPosition + 'px' }"></div>
                <div class="tip-text">{{tipContent }}</div>
                <button class="tip-close" @click="closeTip">✕</button>
              </div>
            </div>
          </transition>
          <div class="career-list">
            <span class="c-tag" :class="{active: activeCareer === 0 }" @click="showCareerTip(0)">
              AI测试开发
            </span>
            <span class="c-tag" :class="{active: activeCareer === 1 }" @click="showCareerTip(1)">
              AI产品测试
            </span>
            <span class="c-tag" :class="{active: activeCareer === 2 }" @click="showCareerTip(2)">
              AI模型测试
            </span>
          </div>
        </div>
      </div>
      <div class="scroll-indicator" @click="scrollToMatrix">
        <span>查看 12 项硬核技术矩阵</span>
        <div class="arrow-down-circle">↓</div>
      </div>
    </header>
    <!-- 第二屏技术矩阵 -->
    <section class="matrix-section" id="matrixSection">
      <h2 class="section-title">全栈AI测开 · 训练测试模型</h2>
      <p class="section-sub">全面成体系 · 从0开始教 · 非怼提示词</p>
      <div class="matrix-grid">
        <div class="matrix-card" @click="showMatrixDetail(0)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🤖
            </div>
          </div>
          <h3 class="matrix-name">AI 生成测试用例</h3>
          <p class="matrix-desc">采纳率 82%</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(1)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🔒
            </div>
          </div>
          <h3 class="matrix-name">AI 代码安全测试</h3>
          <p class="matrix-desc">1万3千条训练数据</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(2)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🔌
            </div>
          </div>
          <h3 class="matrix-name">AI 一键接口测试</h3>
          <p class="matrix-desc">非生成自动化测试脚本</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(3)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🌐
            </div>
          </div>
          <h3 class="matrix-name">AI 一键 WEB 测试</h3>
          <p class="matrix-desc">单步执行3秒</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(4)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🎮
            </div>
          </div>
          <h3 class="matrix-name">AI APP 游戏测试</h3>
          <p class="matrix-desc">毫秒级响应</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(5)">
          <div class="matrix-icon">
            <div class="icon-circle">
              📱
            </div>
          </div>
          <h3 class="matrix-name">AI APP 自动化测试</h3>
          <p class="matrix-desc">单步执行5秒</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(6)">
          <div class="matrix-icon">
            <div class="icon-circle">
              📊
            </div>
          </div>
          <h3 class="matrix-name">AI 测试管理平台</h3>
          <p class="matrix-desc">从0开发，面试必备</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(7)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🧠
            </div>
          </div>
          <h3 class="matrix-name">AI 模型测试</h3>
          <p class="matrix-desc">一键执行测试</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(8)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🔬
            </div>
          </div>
          <h3 class="matrix-name">AI 模型相关</h3>
          <p class="matrix-desc">AI模型测试必备部分内容</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(9)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🔄
            </div>
          </div>
          <h3 class="matrix-name">AI Checkin 内容</h3>
          <p class="matrix-desc">自动触发测试项目全流程管控</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(10)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🤝
            </div>
          </div>
          <h3 class="matrix-name">AI Agent 测试</h3>
          <p class="matrix-desc">行业首个成体系内容</p>
          <div class="matrix-badge">查看详情</div>
        </div>
        <div class="matrix-card" @click="showMatrixDetail(11)">
          <div class="matrix-icon">
            <div class="icon-circle">
              🤔
            </div>
          </div>
          <h3 class="matrix-name">AI 自我反思测试</h3>
          <p class="matrix-desc">自校验测试可灵活配置</p>
          <div class="matrix-badge">查看详情</div>
        </div>
      </div>
    </section>
    <!-- 技术矩阵详情弹窗 -->
    <div class="matrix-dialog-overlay" v-if="showMatrixDialog" @click="closeMatrixDialog">
      <div class="matrix-dialog" @click.stop>
        <div class="matrix-dialog-header">
          <h3>{{ currentMatrixItem.name }}</h3>
          <button class="matrix-dialog-close" @click="closeMatrixDialog">×</button>
        </div>
        <div class="matrix-dialog-body">
          <div class="matrix-dialog-icon-container">
            <div class="matrix-icon-large">
              <div class="icon-circle-large">
                {{ currentMatrixItem.icon }}
              </div>
            </div>
          </div>
          <div class="matrix-dialog-content">
            <div class="matrix-stats">
              <div class="matrix-stat-item">
                <span class="matrix-stat-value">{{ currentMatrixItem.metric }}</span>
              </div>
            </div>
            <div class="matrix-details-grid">
              <!-- 实现方式 -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <div class="detail-icon">📋</div>
                  <h4 class="detail-title">教你学会</h4>
                </div>
                <div class="detail-card-content">
                  <div class="detail-text" v-html="currentMatrixItem.stack"></div>
                </div>
              </div>
              <!-- 技术亮点 -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <div class="detail-icon">✨</div>
                  <h4 class="detail-title">技术亮点</h4>
                </div>
                <div class="detail-card-content">
                  <div class="detail-text">{{ currentMatrixItem.metric }}</div>
                </div>
              </div>
              <!-- 其他说明 -->
              <div class="detail-card">
                <div class="detail-card-header">
                  <div class="detail-icon">📝</div>
                  <h4 class="detail-title">其他说明</h4>
                </div>
                <div class="detail-card-content">
                  <div class="detail-text">{{ currentMatrixItem.note }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 新的表单部分 -->
    <section class="form-section" id="leadForm">
      <div class="form-box">
        <h3>提交后作者会联系您并安排试听</h3>
        <p class="copyright-tip">试听支付2.8元，为您提供高质量AI测开试听</p>
        <!-- 姓名输入 -->
        <div class="input-group">
          <input type="text" v-model="form.name" placeholder="请输入真实姓名（非真实无效）" @input="validateName"/>
          <div class="error-tip" v-if="nameError">{{nameError }}</div>
        </div>
        <!-- 手机号和验证码 -->
        <div class="input-group">
          <input type="tel" v-model="form.phone" placeholder="请输入手机号" maxlength="11" @input="validatePhone"/>
          <div class="error-tip" v-if="phoneError">{{phoneError }}</div>
          <div class="verification-row">
            <input type="text" v-model="form.code" placeholder="请输入验证码" maxlength="4" class="code-input"/>
            <button class="vercode-btn" @click="getVercode" :disabled="verCodeButtonStatus || !canGetCode" :class="{disabled: verCodeButtonStatus || !canGetCode }">
              {{verCodeButtonName }}
            </button>
          </div>
        </div>
        <!-- 支付方式选择 -->
        <div class="payment-type-selector">
          <div class="payment-title">选择支付方式</div>
          <div class="payment-options">
            <div class="payment-option" :class="{'wechat-active': payType === 0, 'alipay-active': payType === 1 }" @click="payType = 0">
              <div class="payment-icon">
                <img src="../../static/wechat.svg" alt="微信支付">
              </div>
              <span>微信支付</span>
            </div>
            <div class="payment-option" :class="{'wechat-active': payType === 0, 'alipay-active': payType === 1 }" @click="payType = 1">
              <div class="payment-icon">
                <img src="../../static/alipay.svg" alt="支付宝">
              </div>
              <span>支付宝</span>
            </div>
          </div>
        </div>
        <!-- 支付按钮 -->
        <button class="submit-btn" @click="handlePayment" :disabled="isLoading || !canSubmit" :class="{disabled: isLoading || !canSubmit }">
          <span v-if="isLoading">处理中...</span>
          <span v-else>提交并支付2.8元，开启高质量试听</span>
        </button>
        <!-- 支付提示 -->
        <div class="payment-tip">
          支付渠道、短信对接、前后端开发由教程作者独立完成
        </div>
      </div>
      <!-- 支付对话框 -->
      <div class="payment-dialog-overlay" v-if="showPaymentDialog">
        <div class="payment-dialog">
          <div class="dialog-header">
            <h3>支付确认</h3>
            <button class="dialog-close" @click="showPaymentDialog = false">×</button>
          </div>
          <div class="dialog-body">
            <div class="payment-info">
              <div class="payment-item">
                <span class="label">课程名称：</span>
                <span class="value">全栈AI测开教程试听</span>
              </div>
              <div class="payment-item">
                <span class="label">支付金额：</span>
                <span class="value price">￥2.80</span>
              </div>
              <div class="payment-item">
                <span class="label">支付方式：</span>
                <span class="value">{{payType === 0 ? '微信支付' : '支付宝' }}</span>
              </div>
            </div>
            <div class="payment-tips">
            <div class="payment-tip-line">
              <span class="tip-icon">↗</span>
              <span>点击确认支付后解锁试听</span>
            </div>
            <div class="payment-tip-line">
              <span class="tip-icon">✓</span>
              <span>此页面所有功能均由测试开发俊哥独立完成，学完你也能独立实现</span>
            </div>
          </div>
            <div class="payment-actions">
              <button class="cancel-btn" @click="showPaymentDialog = false">取消</button>
              <button class="confirm-btn" @click="createUserPayUrl">确认支付</button>
            </div>
          </div>
        </div>
      </div>
      <!-- 验证码提示弹框 -->
      <div class="vercode-dialog-overlay" v-if="showVercodeDialog">
        <div class="vercode-dialog">
          <div class="vercode-dialog-header">
            <h3>验证码提示</h3>
            <button class="vercode-dialog-close" @click="closeVercodeDialog">×</button>
          </div>
          <div class="vercode-dialog-body">
            <div class="vercode-info">
              <div class="vercode-message">{{vercodeMessage }}</div>
            </div>
            <div class="vercode-actions">
              <button class="vercode-confirm-btn" @click="closeVercodeDialog">确定</button>
            </div>
          </div>
        </div>
      </div>
      <!-- 作品展示区域 -->
      <section class="works-section">
        <div class="works-container">
          <h2 class="works-title">学员作品 · 眼见为实</h2>
          <p class="works-subtitle">点击查看学员真实作品展示</p>
          <div class="works-grid">
            <div class="work-card" @click="showWork(0)">
              <div class="work-icon">
                <div class="icon-circle">
                  👨‍🎓
                </div>
              </div>
              <h3 class="work-name">学员作品</h3>
              <p class="work-desc">查看真实学员完成的项目</p>
              <div class="work-badge">查看详情</div>
            </div>
            <div class="work-card" @click="showWork(1)">
              <div class="work-icon">
                <div class="icon-circle">
                  📊
                </div>
              </div>
              <h3 class="work-name">训练模型日志</h3>
              <p class="work-desc">查看训练测试模型日志</p>
              <div class="work-badge">查看详情</div>
            </div>
            <div class="work-card" @click="showWork(2)">
              <div class="work-icon">
                <div class="icon-circle">
                  🔧
                </div>
              </div>
              <h3 class="work-name">测试框架开发</h3>
              <p class="work-desc">查看测试框架截图</p>
              <div class="work-badge">查看详情</div>
            </div>
            <div class="work-card" @click="showWork(3)">
              <div class="work-icon">
                <div class="icon-circle">
                  📈
                </div>
              </div>
              <h3 class="work-name">AI用例指标</h3>
              <p class="work-desc">查看AI生成测试用例数据</p>
              <div class="work-badge">查看详情</div>
            </div>
          </div>
        </div>
      </section>

      <!-- 作品展示弹窗 - 支持多图展示 -->
      <div class="works-dialog-overlay" v-if="showWorksDialog" @click="closeWorksDialog">
        <div class="works-dialog" @click.stop>
          <div class="works-dialog-header">
            <h3>{{ currentWorkData.title }}</h3>
            <button class="works-dialog-close" @click="closeWorksDialog">×</button>
          </div>
          <div class="works-dialog-body">
            <!-- 图片展示区域 -->
            <div class="works-dialog-image-container">
              <!-- 图片加载状态 -->
              <div v-if="imageLoading" class="image-loading">
                <div class="loading-spinner"></div>
                <p>加载中...</p>
              </div>
              <!-- 有图片时显示 -->
              <template v-else>
                <div v-if="currentWorkData.images && currentWorkData.images.length > 0" class="image-gallery">
                  <!-- 当前显示的图片 -->
                  <img :src="currentImageSrc" :alt="currentWorkData.title" class="works-dialog-image" @load="onImageLoad" @error="onImageError">
                  <!-- 图片导航（多张图片时显示） -->
                  <div class="image-navigation" v-if="currentWorkData.images.length > 1"><button class="nav-btn prev-btn" @click="prevImage" :disabled="currentImageIndex === 0">‹</button>
                    <span class="image-counter">
                      {{ currentImageIndex + 1 }}/{{ currentWorkData.images.length }}
                    </span>
                    <button class="nav-btn next-btn" @click="nextImage" :disabled="currentImageIndex === currentWorkData.images.length - 1">›</button>
                  </div>
                </div>
                <!-- 无图片时显示占位符 -->
                <div v-else class="image-placeholder">
                  <div class="placeholder-content">
                    <div class="placeholder-icon">{{ currentWorkData.placeholderIcon }}</div>
                    <p>{{ currentWorkData.placeholderText }}</p>
                  </div>
                </div>
              </template>
            </div>
            <div class="works-dialog-content">
              <!-- 作品描述 -->
              <div class="works-description">
                <h4>内容说明</h4>
                <p>{{ currentWorkData.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- 试听注意事项 -->
    <div class="audition-notice">
      <div class="notice-title">试听须知：</div>
      <div class="notice-item">1、作者将在早10点和晚10点间联系您安排试听，来电为深圳手机号</div>
      <div class="notice-item">2、此价格为体验/试学课价格，具体收费以实际课程信息为准</div>
      <div class="notice-item">3、如24小时内未联系上的朋友，系统将自动退款，试听失效且拒绝再次试听</div>
      <div class="notice-item">4、本内容仅面向21岁以上成年人</div>
    </div>
    <div style="font-size: 8px;text-align: center;padding: 1px;line-height: 1;">
      <img src="/static/logo.ico" alt="公司Logo" style="height: 2em;vertical-align: middle;">
      &copy;2026 张家界市洋么科技有限公司 - 备案号：
      <a href="http://beian.miit.gov.cn" target="_blank">湘ICP备2024068691号</a>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      isLoading: false,
      form: { name: '', phone: '', code: '' },
      nameError: '',
      phoneError: '',
      activeCareer: null,
      showTip: false,
      tipContent: '',
      tipArrowPosition: 50,
      tipContents: [
        '测试进阶第一选择方向，80%学员就业方向，用人方普遍可以给到20K以上',
        'AI时代测试第二选择方向，0基础也可学会，用人方普遍可以给到18K左右',
        '进人工智能公司，对学历有要求，有一定门槛，但薪资很可观，值得选择'
      ],
      tipTimeout: null,
      verCodeButtonName: '获取验证码',
      verCodeButtonStatus: false,
      timer: null,
      countdown: 30,
      showPaymentDialog: false,
      showVercodeDialog: false,
      dyClickId: this.$route.query['clickid'],
      // B站上报ID
      trackid: this.$route.query['track_id'],
      vercodeMessage: '',
      paymentQR: null,
      payType: 0,
      // 技术矩阵数据
      matrixData: [
        {
          name: 'AI 生成测试用例',
          metric: '采纳率 82%',
          stack: '1、训练垂直测试用例模型<br>2、数据提炼及其框架开发<br>3、开发框架服务及测试平台',
          note: '非Prompt、非调大语言模型API',
          icon: '🤖'
        },
        {
          name: 'AI 代码安全测试',
          metric: '1万3千条训练数据',
          stack: '1、训练垂直安全测试模型<br>2、开发AI执行测试框架及平台<br>3、开发一键部署测试脚本',
          note: '支持Python、Java、Vue、C++编程语言',
          icon: '🔒'
        },
        {
          name: 'AI 一键接口测试',
          metric: '非生成自动化测试脚本',
          stack: '1、训练垂直接口测试模型<br>2、训练路径规划测试模型<br>3、开发AI执行测试框架及平台',
          note: '从接口文档到测试报告，一键执行（非生成自动化测试脚本）',
          icon: '🔌'
        },
        {
          name: 'AI 一键 WEB 测试',
          metric: '单步执行3秒，可压缩至2.5秒',
          stack: '1、训练垂直元素或视觉测试模型<br>2、训练路径规划测试模型<br>3、开发AI执行测试框架及平台',
          note: '实现功能测试用例直接执行测试（非生成自动化测试脚本）',
          icon: '🌐'
        },
        {
          name: 'AI APP 游戏测试',
          metric: '毫秒级响应，单步45毫秒左右',
          stack: '1、数据集标记及数据提炼<br>2、训练垂直游戏测试模型<br>3、开发AI APP游戏测试框架',
          note: '实时自动理解及触发测试操作',
          icon: '🎮'
        },
        {
          name: 'AI APP 自动化测试',
          metric: '单步执行5秒，可压缩至3秒',
          stack: '1、训练垂直元素或视觉测试模型<br>2、训练路径规划测试模型<br>3、开发AI执行测试框架及平台',
          note: '实现功能测试用例直接执行测试（非生成自动化测试脚本），提供两套方案',
          icon: '📱'
        },
        {
          name: 'AI 测试管理平台',
          metric: '从0开发，面试必备',
          stack: '1、后端和前端两门编程语言<br>2、集成开发AI测试用例、AI接口测试、AI代码安全测试等技术栈',
          note: '全平台开发',
          icon: '📊'
        },
        {
          name: 'AI 模型测试',
          metric: '一键执行测试',
          stack: '1、AI模型测试框架开发<br>2、多维度测试场景支持<br>3、AI模型性能测试',
          note: '涵盖黑盒、性能测试维度',
          icon: '🧠'
        },
        {
          name: 'AI 模型相关',
          metric: 'AI模型测试必备部分内容',
          stack: '1、机器学习与神经网络<br>2、Pytorch、Transformers<br>3、高性能并发环境部署',
          note: '除AI模型测试外，其他AI测试技术也会涉及',
          icon: '🔬'
        },
        {
          name: 'AI Checkin 内容',
          metric: '测试项目全流程管控',
          stack: '1、AI Git二次开发<br>2、AI CI/CD部署及开发',
          note: '项目全流程管控',
          icon: '🔄'
        },
        {
          name: 'AI Agent 测试',
          metric: '行业首个成体系内容',
          stack: '1、从AI Agent理论与协议开始<br>2、完整开发被测AI Agent项目<br>3、开发AI Agent一键测试框架',
          note: '涵盖黑盒、白盒、性能等测试维度',
          icon: '🤝'
        },
        {
          name: 'AI 自我反思测试',
          metric: '自校验测试可灵活配置',
          stack: '1、AI自我反思测试组件<br>2、AI自校验测试<br>3、AI测试全过程监控',
          note: 'AI测试全过程监控',
          icon: '🤔'
        }
      ],
      showMatrixDialog: false,
      currentMatrixIndex: 0,
      // 作品展示相关数据
      showWorksDialog: false,
      currentWorkIndex: 0,
      currentImageIndex: 0,
      imageLoading: false,
      worksData: [
        {
          title: '学员作品展示',
          description: '每位学员学完之后都能独立开发前后端分离的AI测试平台，这是毕业的标准。鼓励学员开发属于自己风格的作品。',
          placeholderIcon: '👨‍🎓',
          placeholderText: '学员项目作品截图',
          images: [
            '../../static/works/student_project_1.png',
            '../../static/works/student_project_2.jpg',
            '../../static/works/student_project_3.png'
          ]
        },
        {
          title: '训练模型日志',
          description: '我们都知道，堆提示词、调大语言模型API，无法帮助我们落地AI测试技术，所以我们采用训练测试模型的方式。',
          placeholderIcon: '📊',
          placeholderText: '模型训练日志截图',
          images: [
            '../../static/works/training_log_1.png'
          ]
        },
        {
          title: '测试框架开发',
          description: '你将学到真正的技术，从模型开发、执行测试框架开发，这些都会要求你独立从0开发实现，真正锻炼你的硬实力。',
          placeholderIcon: '🔧',
          placeholderText: '测试框架界面截图',
          images: [
            '../../static/works/framework_1.png'
          ]
        },
        {
          title: 'AI生成测试用例指标',
          description: '图片为AI生成测试用例的部分指标数据，学员对指标会比较苛刻，正确而又重复生成的用例我们也会算成错误生成。',
          placeholderIcon: '📈',
          placeholderText: '测试用例指标图表',
          images: [
            '../../static/works/metrics_1.png',
            '../../static/works/metrics_2.png'
          ]
        }
      ]
    }
  },
  computed: {
    canGetCode () {
      return this.form.name && this.form.phone && !this.nameError && !this.phoneError
    },
    canSubmit () {
      return this.form.name && this.form.phone && this.form.code && !this.nameError && !this.phoneError
    },
    currentMatrixItem () {
      return this.matrixData[this.currentMatrixIndex] || {}
    },
    currentWorkData () {
      return this.worksData[this.currentWorkIndex] || {}
    },
    currentImageSrc () {
      if (this.currentWorkData.images && this.currentWorkData.images.length > 0) {
        return this.currentWorkData.images[this.currentImageIndex]
      }
      return ''
    }
  },
  methods: {
    scrollToMatrix () {
      document.getElementById('matrixSection').scrollIntoView({ behavior: 'smooth' })
    },
    scrollToForm () {
      document.getElementById('leadForm').scrollIntoView({ behavior: 'smooth' })
    },
    showMatrixDetail (index) {
      this.currentMatrixIndex = index
      this.showMatrixDialog = true
    },
    closeMatrixDialog () {
      this.showMatrixDialog = false
    },
    showCareerTip (index) {
      this.activeCareer = index
      this.tipContent = this.tipContents[index]
      this.showTip = true
      const tagWidth = 100
      const tagCenter = (index * tagWidth) + (tagWidth / 2)
      this.tipArrowPosition = tagCenter
      if (this.tipTimeout) {
        clearTimeout(this.tipTimeout)
      }
      this.tipTimeout = setTimeout(() => {
        this.closeTip()
      }, 3000)
    },
    closeTip () {
      this.showTip = false
      this.activeCareer = null
      if (this.tipTimeout) {
        clearTimeout(this.tipTimeout)
      }
    },
    validateName () {
      const name = this.form.name.trim()
      if (!name) {
        this.nameError = '请输入姓名'
      } else if (name.length < 2 || name.length > 5) {
        this.nameError = '姓名应为2-5个字符'
      } else if (!/^[\u4e00-\u9fa5]+$/.test(name)) {
        this.nameError = '请输入中文姓名'
      } else {
        this.nameError = ''
      }
    },
    validatePhone () {
      const phone = this.form.phone
      const reg = /^1[3-9]\d{9}$/
      if (!phone) {
        this.phoneError = '请输入手机号'
      } else if (!reg.test(phone)) {
        this.phoneError = '请输入正确的手机号'
      } else {
        this.phoneError = ''
      }
    },
    async getVercode () {
      if (this.verCodeButtonStatus || !this.canGetCode) return
      this.validateName()
      this.validatePhone()
      if (this.nameError || this.phoneError) return
      this.verCodeButtonStatus = true
      this.startCountdown()
      try {
        const formData = new FormData()
        formData.append('p_number', this.form.phone)
        const response = await fetch('/api/ten/msg/sendvercode/', {
          method: 'POST',
          body: formData
        })
        if (!response.ok) {
          if (response.status === 403) {
            this.showVercodeMessage('每个手机号每天只能发送5条短信')
            this.resetCountdown()
            return
          } else if (response.status >= 500) {
            this.showVercodeMessage('服务器错误，请稍后再试')
            this.resetCountdown()
            return
          }
        }
        const result = await response.json()
        if (result.status === 'success') {
          this.showVercodeMessage('验证码已发送到您的手机，请注意查收')
        } else {
          this.showVercodeMessage(result.message || '验证码发送失败')
          this.resetCountdown()
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        this.showVercodeMessage('验证码发送失败，请稍后再试')
        this.resetCountdown()
      }
    },
    async handlePayment () {
      this.validateName()
      this.validatePhone()
      if (this.nameError || this.phoneError) {
        this.showVercodeMessage('请填写正确的信息')
        return
      }
      if (!this.form.code) {
        this.showVercodeMessage('请输入验证码')
        return
      }
      const codeReg = /^\d{4}$/
      if (!codeReg.test(this.form.code)) {
        this.showVercodeMessage('请输入4位数字验证码')
        return
      }
      try {
        this.isLoading = true
        const formData = new FormData()
        formData.append('p_number', this.form.phone)
        formData.append('v_code', this.form.code)
        const response = await fetch('/api/ten/msg/vercode/', {
          method: 'POST',
          body: formData
        })
        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('每个手机号每天只能尝试5次验证码')
          } else if (response.status >= 500) {
            throw new Error('服务器错误，请稍后再试')
          } else {
            throw new Error('验证失败，状态码：' + response.status)
          }
        }
        const result = await response.json()
        if (result.status === 'success') {
          this.isLoading = false
          this.showPaymentDialog = true
        } else {
          throw new Error(result.message || '验证码错误')
        }
      } catch (error) {
        this.isLoading = false
        this.showVercodeMessage(error.message || '验证码错误，请重新输入')
      }
    },
    showVercodeMessage (message) {
      this.vercodeMessage = message
      this.showVercodeDialog = true
    },
    closeVercodeDialog () {
      this.showVercodeDialog = false
      this.vercodeMessage = ''
    },
    startCountdown () {
      this.timer = setInterval(() => {
        this.countdown--
        this.verCodeButtonName = `${this.countdown}秒后重试`
        if (this.countdown <= 0) {
          this.resetCountdown()
        }
      }, 1000)
    },
    resetCountdown () {
      clearInterval(this.timer)
      this.verCodeButtonStatus = false
      this.verCodeButtonName = '获取验证码'
      this.countdown = 30
    },
    async createUserPayUrl () {
      try {
        this.isLoading = true
        const payFormData = new FormData()
        payFormData.append('name', this.form.name)
        payFormData.append('phone', this.form.phone)
        payFormData.append('payType', this.payType)
        payFormData.append('dy_click_id', this.dyClickId)
        payFormData.append('trackid', this.trackid)
        if (this.trackid !== 'undefined') {
          localStorage.setItem('bilibili', 1)
        } else {
          localStorage.setItem('bilibili', 0)
        }
        const response = await fetch('/api/wechat/payment/createuserpayurl/', {
          method: 'POST',
          body: payFormData
        })
        if (!response.ok) {
          if (response.status >= 500) {
            throw new Error('系统异常，请稍后再试')
          } else {
            throw new Error('请求失败，状态码：' + response.status)
          }
        }
        const result = await response.json()
        if (result.code !== 0) {
          throw new Error(result.error || '支付创建失败')
        }
        localStorage.setItem('outTradeNo', result.out_trade_no)
        localStorage.setItem('payType', this.payType)
        this.showPaymentDialog = false
        this.isLoading = false
        let paymentUrl = result.url
        if (this.payType === 0) {
          paymentUrl = paymentUrl + '&redirect_url=https://pay.yangmeai.com/payresult?No=1'
        }
        setTimeout(() => {
          window.location.href = paymentUrl
        }, 300)
      } catch (error) {
        this.isLoading = false
        this.showVercodeMessage(error.message || '支付创建失败，请稍后再试')
      }
    },
    // 作品展示相关方法
    showWork (index) {
      this.currentWorkIndex = index
      this.currentImageIndex = 0
      this.imageLoading = true
      this.showWorksDialog = true
      // 预加载第一张图片
      if (this.currentWorkData.images && this.currentWorkData.images.length > 0) {
        this.preloadImage(this.currentWorkData.images[0])
      } else {
        this.imageLoading = false
      }
    },
    closeWorksDialog () {
      this.showWorksDialog = false
      this.imageLoading = false
    },
    prevImage () {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
        this.imageLoading = true
        this.preloadImage(this.currentWorkData.images[this.currentImageIndex])
      }
    },
    nextImage () {
      if (this.currentWorkData.images && this.currentImageIndex < this.currentWorkData.images.length - 1) {
        this.currentImageIndex++
        this.imageLoading = true
        this.preloadImage(this.currentWorkData.images[this.currentImageIndex])
      }
    },
    preloadImage (src) {
      const img = new Image()
      img.onload = () => {
        this.imageLoading = false
      }
      img.onerror = () => {
        this.imageLoading = false
        console.error(`Failed to load image: ${src}`)
      }
      img.src = src
    },
    onImageLoad () {
      this.imageLoading = false
    },
    onImageError () {
      this.imageLoading = false
      console.error('Image load error')
    }
  },
  beforeDestroy  () {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'DengXian';
  src: local('DengXian'), local('Microsoft YaHei Light')
}

.h5-container {
  background-color: #f2f4f7;
  color: #333;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
  min-height: 100vh;
}

/* 导航 */
.navbar {
  display: flex;justify-content: space-between;align-items: center;
  padding: 15px 20px;background: #fff;position: sticky;top: 0;z-index: 100;
}
.logo-text {font-size: 15px;font-weight: 600;color: #2c3e50;}
.badge {font-size: 10px;background: #333;color: #fff;padding: 2px 8px;border-radius: 20px;margin-left: 5px;}
.consult-btn {background: transparent;border: 1px solid #333;padding: 5px 15px;font-size: 12px;border-radius: 30px;}

/* 第一屏 Hero - 标题同行修改 */
.hero-section {
  padding: 30px 15px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.02);
  margin:10px 10px 10px;
  position: relative;
  z-index: 1;
}
.main-title {
  font-size: 24px;
  color: #000;
  margin-bottom: 25px;
  text-align: center;
  white-space: nowrap;
}
.title-split {color: #ddd;margin: 0 5px;font-weight: 200;}
.sub-highlight {color: #2c68ff;font-weight: 400;}

.feature-box {display: flex;flex-direction: column;gap: 10px;margin-bottom: 30px;}
.feature-item {
  display: flex;
  align-items: center;
  background: #f8f9fb;
  padding: 15px;
  border-radius: 20px;
  position: relative;
}
.feature-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  border-radius: 50%;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  box-shadow: 0 4px 12px rgba(44, 104, 255, 0.2);
}

.feature-icon svg {
  width: 20px;
  height: 20px;
  stroke: white;
  fill: none;
}

/* 如果某些SVG内部有填充色，特别处理 */
.feature-icon svg [fill="currentColor"] {
  fill: white;
}

.icon-dot {width: 6px;height: 6px;background: #2c68ff;border-radius: 50%;margin-right: 12px;}
.feature-item p {margin: 0;font-size: 14px;line-height: 1.4;}
.light-desc {font-size: 12px;color: #888;}

.metrics-row {
  display: flex;justify-content: space-around;align-items: center;
  background: #fcfcfc;padding: 15px 5px;border-radius: 25px;margin: 25px 0;
}
.num {display: block;font-size: 20px;font-weight: 500;color: #000;}
.lbl {font-size: 11px;color: #999;}
.m-divider {width: 1px;height: 20px;background: #eee;}

/* 就业方向 */
.career-bar {
  position: relative;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px dashed #eee;
}
.career-label {
  font-size: 12px;
  color: #aaa;
  text-align: center;
  margin-bottom: 12px;
  position: relative;
  z-index: 2;
}
.career-list {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  position: relative;
  z-index: 1;
}
.c-tag {
  flex: 1;
  background: #333;
  color: #fff;
  font-size: 11px;
  padding: 10px 2px;
  text-align: center;
  border-radius: 40px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.c-tag:active {
  transform: scale(0.95);
}

.c-tag.active {
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(44, 104, 255, 0.3);
  transform: translateY(-2px);
}

/* 就业方向提示弹窗 */
.career-tip {
  position: absolute;
  top: -70px;
  left: 0;
  right: 0;
  z-index: 10;
  margin-bottom: 10px;
}

.tip-content {
  position: relative;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  color: #fff;
  padding: 12px 40px 12px 15px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(44, 104, 255, 0.25);
  margin: 0 10px;
}

.tip-arrow {
  position: absolute;
  bottom: -6px;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  transform: rotate(45deg);
}

.tip-text {
  font-size: 13px;
  line-height: 1.4;
  font-weight: 400;
  text-align: center;
}

.tip-close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.tip-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.tip-fade-enter-active,
.tip-fade-leave-active {
  transition: all 0.3s ease;
}

.tip-fade-enter-from,
.tip-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.scroll-indicator {
  text-align: center;
  margin-top: 30px;
  color: #bbb;
  font-size: 11px;
}
.arrow-down-circle {
  width: 22px;height: 22px;border: 1px solid #eee;
  border-radius: 50%;margin: 8px auto 0;line-height: 20px;
}

/* 第二屏 技术矩阵 */
.matrix-section {
  padding: 20px 15px;
  background: #fff;
  border-radius: 12px;
  margin: 20px 10px 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.02);
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.section-title {
  font-size: 22px;
  text-align: center;
  margin-bottom: 5px;
  color: #000;
  font-weight: 600;
  font-family: 'DengXian', sans-serif;
}

.section-sub {
  text-align: center;
  font-size: 12px;
  color: #888;
  margin-bottom: 20px;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

/* 技术矩阵网格布局 */
.matrix-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 500px;
  margin: 0 auto;
}

.matrix-card {
  background: #f8f9fb;
  border-radius: 16px;
  padding: 18px 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.matrix-card:hover {
  background: #f0f4ff;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(44, 104, 255, 0.1);
}

.matrix-icon {
  margin-bottom: 12px;
}

.matrix-icon .icon-circle {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin: 0 auto;
}

.matrix-name {
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin: 0 0 5px 0;
  font-family: 'DengXian', sans-serif;
}

.matrix-desc {
  font-size: 11px;
  color: #666;
  margin: 0;
  line-height: 1.3;
  max-width: 90%;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.matrix-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #eef4ff;
  color: #2c68ff;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
  font-family: 'DengXian', sans-serif;
}

/* 技术矩阵详情弹窗样式 */
.matrix-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
  font-family: 'DengXian', sans-serif;
}

.matrix-dialog {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  max-height: 65vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.matrix-dialog-header {
  background: #1a1a1a;
  color: #fff;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
  font-family: 'DengXian', sans-serif;
}

.matrix-dialog-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  width: 100%;
  text-align: center;
  font-family: 'DengXian', sans-serif;
}

.matrix-dialog-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-family: 'DengXian', sans-serif;
}

.matrix-dialog-body {
  padding: 12px 20px;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.matrix-dialog-icon-container {
  text-align: center;
  margin-bottom: 10px;
}

.matrix-icon-large {
  display: inline-block;
}

.icon-circle-large {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto;
}

.matrix-dialog-content {
  font-family: 'DengXian', sans-serif;
}

.matrix-stats {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.matrix-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #eef2ff 100%);
  border-radius: 10px;
  padding: 10px 15px;
  min-width: 140px;
  border: 1px solid rgba(44, 104, 255, 0.1);
}

.matrix-stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #2c68ff;
  margin-bottom: 2px;
  font-family: 'DengXian', sans-serif;
}

/* 重新设计的详情卡片网格 */
.matrix-details-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eef2ff;
  box-shadow: 0 2px 8px rgba(44, 104, 255, 0.05);
}

.detail-card-header {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  padding: 10px 12px;
  border-bottom: 1px solid #eef2ff;
}

.detail-icon {
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-right: 8px;
  color: #fff;
  flex-shrink: 0;
}

.detail-title {
  font-size: 14px;
  color: #000;
  margin: 0;
  font-weight: 600;
  font-family: 'DengXian', sans-serif;
}

.detail-card-content {
  padding: 12px;
  text-align: left;
  max-height: 150px;
  overflow-y: auto;
}

.detail-text {
  font-size: 12px;
  color: #333;
  line-height: 1.4;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.detail-text div {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* 底部表单部分 */
.form-section {
  padding: 10px;
}

.form-box {
  background: #1a1a1a;
  color: #fff;
  padding: 30px 20px;
  border-radius: 12px;
  text-align: center;
}

.form-box h3 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #fff;
}

.copyright-tip {
  font-size: 12px;
  color: #888;
  margin-bottom: 25px;
}

/* 输入框组 */
.input-group {
  margin-bottom: 20px;
}

.input-group input {
  width: 100%;
  padding: 14px;
  border-radius: 15px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  text-align: left;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 5px;
}

.input-group input::placeholder {
  color: #aaa;
}

/* 验证码行 */
.verification-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.code-input {
  flex: 1;
}

.vercode-btn {
  flex: 0 0 100px;
  background: #2c68ff;
  color: #fff;
  border: none;
  border-radius: 15px;
  padding: 14px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.vercode-btn.disabled {
  background: #666;
  cursor: not-allowed;
  opacity: 0.7;
}

/* 错误提示 */
.error-tip {
  color: #ff6b6b;
  font-size: 12px;
  text-align: left;
  margin-top: 5px;
  padding-left: 5px;
}

/* 支付类型选择 */
.payment-type-selector {
  margin-bottom: 20px;
}

.payment-title {
  font-size: 14px;
  color: #aaa;
  margin-bottom: 10px;
  text-align: left;
}

.payment-options {
  display: flex;
  gap: 10px;
}

.payment-option {
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.payment-option.active {
  background: rgba(44, 104, 255, 0.2);
  border-color: #2c68ff;
}

.payment-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-icon img {
  width: 100%;
  height: 100%;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.payment-option span {
  font-size: 12px;
  color: #fff;
  transition: all 0.3s ease;
}

/* 微信支付高亮样式 */
.payment-option:nth-child(1).wechat-active {
  background: rgba(7, 193, 96, 0.1);
  border-color: #07C160;
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.2);
}

.payment-option:nth-child(1).wechat-active .payment-icon img {
  filter: none;
}

.payment-option:nth-child(1).wechat-active span {
  color: #07C160;
  font-weight: 600;
}

/* 支付宝高亮样式 */
.payment-option:nth-child(2).alipay-active {
  background: rgba(22, 119, 255, 0.1);
  border-color: #1677FF;
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.2);
}

.payment-option:nth-child(2).alipay-active .payment-icon img {
  filter: none;
}

.payment-option:nth-child(2).alipay-active span {
  color: #1677FF;
  font-weight: 600;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  background: #2c68ff;
  color: #fff;
  border: none;
  padding: 16px;
  border-radius: 15px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.submit-btn.disabled {
  background: #666;
  cursor: not-allowed;
  opacity: 0.7;
}

/* 支付提示 */
.payment-tip {
  font-size: 12px;
  color: #888;
  margin-top: 15px;
  text-align: center;
}

/* 支付对话框 */
.payment-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.payment-dialog {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  animation: slideUp 0.3s ease;
  transform: translateY(0);
}

/* 对话框头部样式调整 */
.dialog-header {
  background: #1a1a1a;
  color: #fff;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: center;
}

.dialog-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
}

.dialog-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.dialog-body {
  padding: 20px;
}

.payment-info {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
}

.payment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.payment-item:last-child {
  border-bottom: none;
}

.payment-item .label {
  color: #666;
  font-size: 14px;
}

.payment-item .value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.payment-item .price {
  color: #2c68ff;
  font-size: 18px;
  font-weight: 600;
}

.payment-tips {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 20px 15px;
  margin: 20px 0;
  text-align: center;
}

.payment-tip-line {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 15px;
  color: #333;
}

.payment-tip-line:last-child {
  margin-bottom: 0;
}

.tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  font-size: 14px;
}

.payment-tip-line:first-child .tip-icon {
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  color: white;
  border-radius: 50%;
  font-weight: bold;
}

.payment-tip-line:last-child .tip-icon {
  color: #2c68ff;
  font-weight: bold;
}

.cancel-btn, .confirm-btn {
  flex: 1;
  padding: 14px;
  border-radius: 15px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.cancel-btn {
  background: #f0f0f0;
  color: #333;
}

.confirm-btn {
  background: #2c68ff;
  color: #fff;
}

/* 验证码提示弹框样式 */
.vercode-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.vercode-dialog {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 320px;
  overflow: hidden;
  animation: slideUp 0.3s ease;
  transform: translateY(0);
}

.vercode-dialog-header {
  background: #1a1a1a;
  color: #fff;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.vercode-dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: center;
}

.vercode-dialog-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
}

.vercode-dialog-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.vercode-dialog-body {
  padding: 30px 20px;
}

.vercode-info {
  text-align: center;
  margin-bottom: 25px;
}

.vercode-message {
  font-size: 16px;
  color: #333;
  line-height: 1.5;
  padding: 15px 0;
}

.vercode-actions {
  display: flex;
  justify-content: center;
}

.vercode-confirm-btn {
  background: #2c68ff;
  color: #fff;
  border: none;
  padding: 12px 40px;
  border-radius: 15px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.vercode-confirm-btn:hover {
  background: #1a56ff;
}

/* 试听注意事项样式 */
.audition-notice {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 15px 20px;
  margin: 10px 10px 10px;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}

.notice-title {
  font-size: 13px;
  color: #666;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: left;
}

.notice-item {
  font-size: 12px;
  color: #888;
  line-height: 1.5;
  margin-bottom: 5px;
  text-align: left;
  position: relative;
}

/* 作品展示区域样式 */
.works-section {
  padding: 20px 15px;
  background: #fff;
  border-radius: 12px;
  margin: 20px 0px 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.02);
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.works-container {
  max-width: 600px;
  margin: 0 auto;
}

.works-title {
  font-size: 22px;
  text-align: center;
  margin-bottom: 5px;
  color: #000;
  font-weight: 600;
  font-family: 'DengXian', sans-serif;
}

.works-subtitle {
  text-align: center;
  font-size: 12px;
  color: #888;
  margin-bottom: 20px;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

/* 优化为一排两个的网格布局 */
.works-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 500px;
  margin: 0 auto;
}

.work-card {
  background: #f8f9fb;
  border-radius: 16px;
  padding: 18px 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.work-card:hover {
  background: #f0f4ff;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(44, 104, 255, 0.1);
}

.work-icon {
  margin-bottom: 12px;
}

.icon-circle {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #2c68ff 0%, #6a11cb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin: 0 auto;
}

.work-name {
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin: 0 0 5px 0;
  font-family: 'DengXian', sans-serif;
}

.work-desc {
  font-size: 11px;
  color: #666;
  margin: 0;
  line-height: 1.3;
  max-width: 90%;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.work-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #eef4ff;
  color: #2c68ff;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
  font-family: 'DengXian', sans-serif;
}

/* 作品展示弹窗样式 */
.works-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
  font-family: 'DengXian', sans-serif;
}

.works-dialog {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.works-dialog-header {
  background: #1a1a1a;
  color: #fff;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
  font-family: 'DengXian', sans-serif;
}

.works-dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: center;
  font-family: 'DengXian', sans-serif;
}

.works-dialog-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-family: 'DengXian', sans-serif;
}

.works-dialog-body {
  padding: 20px;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.works-dialog-image-container {
  position: relative;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.works-dialog-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

.image-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #eef2ff;
  border-top-color: #2c68ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.image-gallery {
  width: 100%;
  height: 100%;
  position: relative;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%);
  font-family: 'DengXian', sans-serif;
}

.placeholder-content {
  text-align: center;
  color: #666;
  font-family: 'DengXian', sans-serif;
}

.placeholder-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.placeholder-content p {
  font-family: 'DengXian', sans-serif;
  font-size: 14px;
  margin: 0;
}

.image-navigation {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  padding: 5px 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: #fff;
  font-family: 'DengXian', sans-serif;
}

.nav-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DengXian', sans-serif;
  transition: opacity 0.2s ease;
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-btn:not(:disabled):hover {
  opacity: 0.8;
}

.image-counter {
  font-size: 12px;
  font-weight: 500;
  font-family: 'DengXian', sans-serif;
}

.works-stats {
  display: flex;
  justify-content: space-around;
  background: #f8f9fb;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
  font-family: 'DengXian', sans-serif;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #2c68ff;
  margin-bottom: 5px;
  font-family: 'DengXian', sans-serif;
}

.stat-label {
  font-size: 11px;
  color: #666;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.works-description,
.works-technologies {
  margin-bottom: 20px;
  font-family: 'DengXian', sans-serif;
}

.works-description h4,
.works-technologies h4 {
  font-size: 16px;
  color: #000;
  margin: 0 0 10px 0;
  font-weight: 600;
  font-family: 'DengXian', sans-serif;
}

.works-description p {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tech-tag {
  background: #eef4ff;
  color: #2c68ff;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 20px;
  font-family: 'DengXian', sans-serif;
  font-weight: 300;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式调整 */
@media (max-width: 480px) {
  .matrix-grid,
  .works-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .matrix-card,
  .work-card {
    padding: 15px 10px;
    min-height: 140px;
  }
  .matrix-icon .icon-circle,
  .icon-circle {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  .matrix-name,
  .work-name {
    font-size: 13px;
  }
  .matrix-desc,
  .work-desc {
    font-size: 10px;
  }
  .matrix-dialog {
    max-height: 75vh;
    max-width: 95%;
  }
  .icon-circle-large {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
  .matrix-stat-value {
    font-size: 16px;
  }
  .detail-card-header {
    padding: 10px 12px;
  }
  .detail-icon {
    width: 22px;
    height: 22px;
    font-size: 12px;
    margin-right: 8px;
  }
  .detail-title {
    font-size: 14px;
  }
  .detail-card-content {
    padding: 12px;
    max-height: 120px;
  }
  .detail-text {
    font-size: 12px;
  }
  .works-dialog {
    max-height: 85vh;
    max-width: 95%;
  }
  .works-dialog-image-container {
    height: 250px;
  }
  .stat-value {
    font-size: 16px;
  }
}

@media (max-width: 375px) {
  .verification-row {
    flex-direction: column;
  }
  .vercode-btn {
    flex: 1;
    width: 100%;
  }
  .works-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .work-card {
    padding: 12px 8px;
    min-height: 130px;
  }
  .works-dialog-image-container {
    height: 200px;
  }
  .tech-tag {
    font-size: 11px;
    padding: 4px 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/index.vue